import React, { useState, useEffect, useCallback } from 'react';
import { getMerchantInfo, createPaymentReportSession } from '../services/paymentsService';

const PaymentReportTab = ({ practiceId }) => {
  const [merchantInfo, setMerchantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [merchantNotFound, setMerchantNotFound] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);

  const loadMerchantData = useCallback(async () => {
    try {
      setError('');
      const merchantData = await getMerchantInfo(practiceId);
      
      // Check the merchant_status from the API response
      if (merchantData?.merchant_status === 'not_found') {
        setMerchantNotFound(true);
        setMerchantInfo(null);
      } else if (merchantData?.merchant_status === 'found') {
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(false);
      } else {
        // Fallback for older API responses that don't have merchant_status
        setMerchantInfo(merchantData?.merchant || null);
        setMerchantNotFound(!merchantData?.merchant);
      }
    } catch (err) {
      setError('Failed to load merchant data. Please check your connection.');
      setMerchantInfo(null);
      setMerchantNotFound(false);
    }
  }, [practiceId]);

  const loadPaymentReport = useCallback(async () => {
    try {
      setCreatingSession(true);
      setError('');
      
      // Create a session for the Rainforest payment report component
      const sessionResponse = await createPaymentReportSession(practiceId);
      setSessionData(sessionResponse);
      
    } catch (err) {
      setError('Failed to load payment report. Please try again.');
    } finally {
      setCreatingSession(false);
    }
  }, [practiceId]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await loadMerchantData();
      } catch (err) {
        setError('Failed to load merchant data. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    if (practiceId) {
      loadData();
    }
  }, [practiceId, loadMerchantData]);

  // Auto-load payment report if merchant is found
  useEffect(() => {
    if (merchantInfo && !merchantNotFound && !sessionData && !creatingSession) {
      loadPaymentReport();
    }
  }, [merchantInfo, merchantNotFound, sessionData, creatingSession, loadPaymentReport]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading payment report...</p>
      </div>
    );
  }

  return (
    <div>
      {error && <p style={{ color: '#b42318', marginBottom: '20px' }}>⚠️ {error}</p>}

      {/* Payment Report Section */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        {merchantInfo && !merchantNotFound ? (
          <div>
            {sessionData && sessionData.session_key ? (
              <div>
                <div style={{ 
                  backgroundColor: '#e6efe9', 
                  padding: '15px', 
                  borderRadius: '4px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#1f5a3d' }}>Payment Report</h4>
                  <p style={{ margin: '0', fontSize: '0.9rem', color: '#3c4540' }}>
                    View all payments and refunds for your merchant account. You can filter by date, status, and more.
                  </p>
                </div>

                {/* Rainforest Payment Report Component */}
                <div style={{ 
                  border: '1px solid #e3e7e4', 
                  padding: '20px', 
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  minHeight: '600px'
                }}>
                  <rainforest-payment-report
                    session-key={sessionData.session_key}
                    data-filters={JSON.stringify({ merchant_id: sessionData.merchant_id })}
                    show-export-button
                  ></rainforest-payment-report>
                </div>
              </div>
            ) : creatingSession ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                backgroundColor: '#f7f8f7',
                borderRadius: '8px'
              }}>
                <p style={{ fontSize: '1.1rem', color: '#3c4540' }}>Loading payment report...</p>
              </div>
            ) : (
              <div style={{ 
                backgroundColor: '#fdf3e3', 
                padding: '15px', 
                borderRadius: '4px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#a8520c' }}>
                  ⚠️ Unable to Load Payment Report
                </h4>
                <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#a8520c' }}>
                  There was an issue loading the payment report. Please try again.
                </p>
                <button 
                  onClick={loadPaymentReport}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#1f5a3d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  🔄 Retry
                </button>
              </div>
            )}
          </div>
        ) : merchantNotFound ? (
          <div>
            <div style={{ 
              backgroundColor: '#fdf3e3', 
              padding: '15px', 
              borderRadius: '4px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#a8520c' }}>
                ⚠️ No Merchant Setup Found
              </h4>
              <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#a8520c' }}>
                This practice doesn't have payment processing setup yet. Please set up your merchant account 
                in the Merchant tab first to view payment reports.
              </p>
            </div>
          </div>
        ) : (
          <p style={{ color: '#6b716d', fontStyle: 'italic' }}>
            Loading merchant information...
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentReportTab;

